import React, { useCallback, useMemo, useState } from 'react';
import { format } from 'date-fns';
import {
  BusFrontIcon,
  ChevronDownIcon,
  PencilIcon,
  FullscreenIcon,
  QrCodeIcon,
} from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import { Link, useRouter } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { CompanyDocumentDetailQueryResponse } from '@area-restrita/api-client/model/companyDocumentDetailQueryResponse';
import { getGetDocumentFileQueryKey } from '@area-restrita/api-client/endpoints/document-access/document-access.gen';
import {
  useUpdateCompanyDocument,
  getGetCompanyDocumentByIdQueryKey,
  getGetCompanyDocumentRelatedCompaniesQueryKey,
  useGetCompanyDocumentRelatedCompanies,
} from '@area-restrita/api-client/endpoints/company-document/company-document.gen';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import DocumentCategorySelector from '@/components/QuerySelectors/DocumentCategorySelector';
import BuslineSelector, {
  SelectedBusline,
} from '@/components/QuerySelectors/BuslineSelector';
import FileInput from '@/components/FileInput';
import { Form, FormField } from '@/components/ui/form';
import type { SelectOption } from '@/components/Select';
import { DocumentViewer } from '@/modules/documents/components/document-viewer/DocumentViewer';
import { useDocumentFile } from '@/modules/documents/hooks/useDocumentFile';
import { useAuthSessionStore } from '@/modules/authentication/stores/authSessionStore';

import {
  ICompanyDocument,
  companyDocumentSchema,
} from '../../components/create-company-document/schema';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface IProps {
  companyDocument: CompanyDocumentDetailQueryResponse;
}

const toBuslineOption = (busline: SelectedBusline): SelectOption => ({
  value: busline.id,
  label: `${busline.code} - ${busline.name}`,
});

interface IRouteLinkProps {
  buslineId: number;
  isCompanyUser: boolean;
  children: React.ReactNode;
}

const RouteLink: React.FC<IRouteLinkProps> = ({
  buslineId,
  isCompanyUser,
  children,
}) =>
  isCompanyUser ? (
    <Link
      to="/busline-schedules"
      search={{
        filters: [{ id: 'buslineId', value: [buslineId] }],
      }}
    >
      {children}
    </Link>
  ) : (
    <Link to="/buslines/$buslineId" params={{ buslineId }}>
      {children}
    </Link>
  );

const CampanyDocumentOverview: React.FC<IProps> = ({ companyDocument }) => {
  const router = useRouter();
  const { checkAuthorization, session } = useAuthSessionStore();
  const isCompanyUser = Boolean(session?.organization?.company);

  const selectedBuslineOptions = useMemo(
    () => companyDocument.buslines.map(toBuslineOption),
    [companyDocument.buslines],
  );

  const form = useForm<ICompanyDocument>({
    defaultValues: {
      name: companyDocument.name,
      documentCategoryId: {
        value: companyDocument.documentCategory.id ?? '',
        label: companyDocument.documentCategory.description ?? '',
      },
      buslineIds: selectedBuslineOptions,
    },
    resolver: zodResolver(companyDocumentSchema),
  });
  const [isEditing, setIsEditing] = useState(false);

  const { objectUrl, isLoading: documentLoading } = useDocumentFile(
    companyDocument.publicId,
  );
  const relatedCompaniesQuery = useGetCompanyDocumentRelatedCompanies(
    companyDocument.publicId,
    {
      query: {
        enabled: isEditing,
      },
    },
  );
  const buslineCompanyIds = !relatedCompaniesQuery.data
    ? []
    : relatedCompaniesQuery.data.allCompanies
      ? undefined
      : relatedCompaniesQuery.data.companyIds;

  const queryClient = useQueryClient();
  const { mutateAsync } = useUpdateCompanyDocument({
    mutation: {
      onSuccess: async () => {
        await Promise.all(
          [
            ['getCompanyDocuments'],
            ['getInfiniteCompanyDocuments'],
            getGetCompanyDocumentByIdQueryKey(companyDocument.publicId),
            getGetDocumentFileQueryKey(companyDocument.publicId),
            getGetCompanyDocumentRelatedCompaniesQueryKey(
              companyDocument.publicId,
            ),
          ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
        );
        toast.success('Documento atualizado com sucesso');
        router.invalidate();
        handleCancel();
      },
      onError: () => {
        toast.error('Não foi possível atualizar o documento');
      },
    },
  });

  const handleEdit = useCallback(() => {
    form.reset({
      name: companyDocument.name,
      documentCategoryId: {
        value: companyDocument.documentCategory.id ?? '',
        label: companyDocument.documentCategory.description ?? '',
      },
      buslineIds: selectedBuslineOptions,
    });

    setIsEditing(true);
  }, [form, companyDocument, selectedBuslineOptions]);

  const handleCancel = useCallback(() => setIsEditing(false), []);

  const handleSubmit = useCallback<SubmitHandler<ICompanyDocument>>(
    (data) =>
      mutateAsync({
        id: companyDocument.publicId,
        data: {
          name: data.name,
          documentCategoryId: Number(data.documentCategoryId.value),
          buslineIds: data.buslineIds?.map(({ value }) => Number(value)) ?? [],
          file: data.file,
        },
      }),
    [companyDocument.publicId, mutateAsync],
  );

  const canUpdate = checkAuthorization(({ can }) => can('update', 'documents'));

  return (
    <Form {...form}>
      <Card className="flex-1">
        <div className="flex items-center justify-between">
          <CardHeader>
            <CardTitle>
              {isEditing ? (
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <Input
                      className="text-2xl font-bold"
                      disabled={form.formState.isSubmitting}
                      {...field}
                    />
                  )}
                />
              ) : (
                companyDocument.name
              )}
            </CardTitle>
            <CardDescription>
              Identificador do documento: {companyDocument.publicId}
            </CardDescription>
          </CardHeader>
          <div className="flex flex-row items-center gap-4 p-6">
            {isEditing ? (
              <>
                <Button
                  size="sm"
                  onClick={form.handleSubmit(handleSubmit)}
                  disabled={form.formState.isSubmitting}
                >
                  Salvar
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleCancel}
                  disabled={form.formState.isSubmitting}
                >
                  Cancelar
                </Button>
              </>
            ) : (
              canUpdate && (
                <Button size="icon-sm" className="mt-0" onClick={handleEdit}>
                  <PencilIcon className="h-4 w-4" />
                </Button>
              )
            )}
          </div>
        </div>
        <CardContent className="space-y-4">
          <div className="flex-1 space-y-4 flex flex-row items-baseline justify-between gap-32">
            <div className="flex flex-col w-full max-w-60">
              <Label htmlFor="category" className="text-lg font-medium">
                Categoria
              </Label>
              {isEditing ? (
                <FormField
                  control={form.control}
                  name="documentCategoryId"
                  render={({ field }) => (
                    <DocumentCategorySelector
                      disabled={form.formState.isSubmitting}
                      disableOnSingle
                      {...field}
                    />
                  )}
                />
              ) : (
                <div className="text-sm font-medium">
                  {companyDocument.documentCategory.description}
                </div>
              )}
            </div>
            {isEditing && (
              <div className="flex flex-col w-full max-w-60">
                <Label htmlFor="buslines" className="text-lg font-medium">
                  Linhas
                </Label>
                <FormField
                  control={form.control}
                  name="buslineIds"
                  render={({ field }) => (
                    <BuslineSelector
                      multiple
                      companyIds={buslineCompanyIds}
                      selectedBuslines={companyDocument.buslines}
                      disabled={
                        form.formState.isSubmitting ||
                        relatedCompaniesQuery.isLoading
                      }
                      permissionWarningMessage="Esta rota não possui mais permissão para este documento e deve ser removida."
                      {...field}
                    />
                  )}
                />
              </div>
            )}
            <div>
              <Label htmlFor="lastUpdated" className="text-lg font-medium">
                Atualizado em
              </Label>
              <div className="text-sm font-medium">
                {format(companyDocument.updatedAt, 'PPP')}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4 pt-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="category" className="text-lg font-medium">
                Arquivo
              </Label>

              <div className="flex gap-4 justify-end">
                {!isEditing && objectUrl && (
                  <Button size="icon-sm" className="mt-0 hidden">
                    <QrCodeIcon className="h-5 w-5" />
                  </Button>
                )}

                {!isEditing && companyDocument.buslines.length === 1 && (
                  <RouteLink
                    buslineId={companyDocument.buslines[0].id}
                    isCompanyUser={isCompanyUser}
                  >
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button size="sm" className="mt-0">
                            <BusFrontIcon className="h-4 w-4" />
                            Ver rota
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {companyDocument.buslines[0].code} -{' '}
                          {companyDocument.buslines[0].name}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </RouteLink>
                )}

                {!isEditing && companyDocument.buslines.length > 1 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" className="mt-0">
                        <BusFrontIcon className="h-4 w-4" />
                        Rotas
                        <ChevronDownIcon className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuGroup>
                        {companyDocument.buslines.map((busline) => (
                          <DropdownMenuItem key={busline.id} asChild>
                            <RouteLink
                              buslineId={busline.id}
                              isCompanyUser={isCompanyUser}
                            >
                              {busline.code} - {busline.name}
                            </RouteLink>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {!isEditing && objectUrl && (
                  <Link
                    to="/docs/$publicId"
                    params={{ publicId: companyDocument.publicId }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button size="icon-sm" className="mt-0">
                      <FullscreenIcon className="h-5 w-5" />
                    </Button>
                  </Link>
                )}
              </div>
            </div>
            {isEditing ? (
              <FormField
                control={form.control}
                name="file"
                render={({ field }) => (
                  <FileInput
                    disabled={form.formState.isSubmitting}
                    placeholder={
                      <>
                        Clique para adicionar um arquivo <br />
                        <span className="text-sm">
                          Deixe em branco para manter o arquivo atual
                        </span>
                      </>
                    }
                    {...field}
                  />
                )}
              />
            ) : documentLoading ? (
              <div className="flex min-h-64 items-center justify-center rounded-lg border border-dashed border-gray-400 text-sm text-gray-500">
                Carregando arquivo...
              </div>
            ) : objectUrl ? (
              <DocumentViewer
                objectUrl={objectUrl}
                contentType={companyDocument.contentType}
                filename={companyDocument.name}
                height="700px"
              />
            ) : null}
          </div>
        </CardContent>
      </Card>
    </Form>
  );
};

export default CampanyDocumentOverview;
